import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { getStoredUser } from './user-storage.util';

// Routes a locked (welcomeEmailPending === true) user may still reach:
// 'services-zones' to complete service + zone setup (lifted out of manage-account
// into its own page), 'manageaccount' for the rest of their profile, and
// 'contact' to reach support if they get stuck.
const ROUTES_ALLOWED_WHILE_LOCKED = [
  'services-zones',
  'manageaccount',
  'contact',
];

/**
 * Navigation lock for new vendors who have not finished service + zone setup.
 *
 * While the stored user's `welcomeEmailPending` flag is an explicit boolean
 * `true` (armed at registration by connect-service, flipped to `false` exactly
 * once at first profile completion), any authenticated route except the
 * allow-listed ones redirects to the Services & Zones page: `services-zones`
 * stays reachable (service + zone setup happens there), `manageaccount` stays
 * reachable (the rest of the profile) and `contact` stays reachable (so a stuck
 * user can still reach support).
 *
 * Fail-open: only an explicit `=== true` locks. `false`, `undefined`, a missing
 * field, or no stored user all allow navigation — so existing users (no flag)
 * and any backend hiccup degrade to free, never locked out. Login is handled by
 * {@link AuthGuard}; this guard is additive and never touches authentication.
 *
 * Naming: this is a functional guard (`CanActivateFn`) — Angular's recommended
 * style since v15, where the class-based `CanActivate` interface was deprecated.
 * Hence the camelCase const (`setupLockGuard`), unlike the older class-based
 * `AuthGuard` (PascalCase). The two styles coexisting in `canActivate` is
 * intentional, not a typo.
 */
export const setupLockGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): boolean | UrlTree => {
  if (ROUTES_ALLOWED_WHILE_LOCKED.includes(route.routeConfig?.path ?? '')) {
    return true;
  }

  const user = getStoredUser();
  if (user?.welcomeEmailPending === true) {
    const router = inject(Router);
    return router.createUrlTree(['/services-zones']);
  }

  return true;
};
